import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Skeleton } from '@/shared/ui';

import { i18n, type FullHeadMPRErrorCode } from '../../config';

import styles from './FullHeadMPRStatus.module.scss';

type FullHeadMPRStatusProps = {
  errorMessage: FullHeadMPRErrorCode | null;
  isReady: boolean;
  className?: string;
  isSingleSkeleton?: boolean;
  onRetry: () => void;
};

export const FullHeadMPRStatus: FC<FullHeadMPRStatusProps> = (props) => {
  const {
    errorMessage,
    isReady,
    className,
    isSingleSkeleton = false,
    onRetry,
  } = props;
  const { formatMessage } = useIntl();

  if (errorMessage) {
    return (
      <div
        className={cn(styles.loadingOverlay, styles.errorMessage, className)}
        role="alert"
      >
        <FormattedMessage {...i18n.fullHeadMprErrorMessages[errorMessage]} />
        <Button size="small" variant="secondary" onClick={onRetry}>
          <FormattedMessage id="global.retry" defaultMessage="Try again" />
        </Button>
      </div>
    );
  }

  if (isReady) {
    return null;
  }

  return (
    <div
      className={cn(
        styles.loadingOverlay,
        styles.skeletons,
        !isSingleSkeleton && styles.paneSkeletons,
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label={formatMessage({
        id: 'global.loading',
        defaultMessage: 'Loading…',
      })}
    >
      <Skeleton
        className={cn(!isSingleSkeleton && styles.primarySkeleton)}
        width="100%"
        height="100%"
        borderRadius={16}
      />
      {!isSingleSkeleton && (
        <>
          <Skeleton width="100%" height="100%" borderRadius={16} />
          <Skeleton width="100%" height="100%" borderRadius={16} />
        </>
      )}
    </div>
  );
};
